import { Box, Stack, Typography, Button, Link } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import { nextStage, switchFlow } from '../../../../redux/tatWizardSlice';
import {
  ZONES,
  filterPricedCodes, findStGroup,
} from '../constants';
import { useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';

function StatCard({ label, value, sub }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 160,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.75,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10.5 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontSize: 24, fontWeight: 800, mt: 0.5, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11.5 }}>{sub}</Typography>
      )}
    </Box>
  );
}

export default function Stage1Continuation() {
  const dispatch = useDispatch();
  const flowMode = useSelector((s) => s.tatWizard.flowMode);
  const merchantName = useSelector((s) => s.tatWizard.merchantName);
  const merchantOid = useSelector((s) => s.tatWizard.merchantOid);
  const selectedSTs = useSelector((s) => s.tatWizard.selectedSTs);
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const { data: groups = [] } = useGetServiceabilityGroupsQuery();

  const isExisting = flowMode === 'existing';

  // Count only the account codes the merchant actually has pricing for — the same
  // priced-keys scope Stage 2/3/4 use — so this matches the CSVs Stage 4 generates
  // rather than every code in the CP master for these service types. (Empty pricedKeys
  // = pricing not loaded → filterPricedCodes falls back to all, same as before.)
  const accountCodes = (selectedSTs || []).reduce((acc, st) => {
    const active = (findStGroup(groups, st)?.linkedCodes || []).filter((lc) => lc.isActive !== false);
    return acc + filterPricedCodes(active, pricedKeys).length;
  }, 0);
  const tone = isExisting ? 'info' : 'success';

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {isExisting ? (
            <>
              The editor in the next step is prepopulated with TATs already live in ProShip for <b>{merchantName}</b>.
              Cells with the blue source stripe came from the system — edit any you want to change; unchanged cells are
              re-pushed as-is. Each service type&apos;s mode (<b>Blended</b> vs <b>Courier-wise</b>) is carried over and
              can be overridden per service type.
            </>
          ) : (
            <>
              The Pricing Setup has committed rate cards & T&Cs for <b>{merchantName}</b>.
              We will now collect Min/Max delivery TATs per zone per courier account code, AI-extract
              what we can from the signed agreement, and publish to ProShip. Each service type&apos;s
              mode (<b>Blended</b> vs <b>Courier-wise</b>) is auto-detected from your Pricing setup and
              can be overridden per service type.
            </>
          )}
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: `${tone}.lighter`,
          border: 1,
          borderColor: `${tone}.light`,
          borderRadius: 2,
          p: 2.25,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'flex-start',
        }}
      >
        <CheckCircleIcon sx={{ color: `${tone}.main`, fontSize: 30, mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontSize: 15, fontWeight: 700, color: `${tone}.dark` }}>
            {isExisting ? `Loaded TATs for ${merchantName}` : `Pricing successfully uploaded for ${merchantName}`}
          </Typography>
          <Typography variant="body2" sx={{ color: `${tone}.dark`, mt: 0.5, fontSize: 13 }}>
            {isExisting
              ? `${accountCodes} account codes · merchantId `
              : `${accountCodes} courier account codes are ready for TAT capture across ${(selectedSTs || []).length} service types · merchantId `}
            <Box
              component="code"
              sx={{
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                fontSize: 11.5,
                bgcolor: 'background.paper',
                color: 'text.primary',
                px: 0.65,
                py: 0.15,
                borderRadius: 0.5,
                border: 1,
                borderColor: `${tone}.light`,
                wordBreak: 'break-all',
              }}
            >
              {merchantOid}
            </Box>
          </Typography>
        </Box>
        <Button
          variant="contained"
          color={tone}
          endIcon={<ArrowForwardIcon />}
          onClick={() => dispatch(nextStage())}
          sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          {isExisting ? 'Continue to TAT Editor' : 'Continue to TAT Setup'}
        </Button>
      </Box>

      <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1.5}>
        <StatCard label="Merchant" value={merchantName} sub={isExisting ? 'Selected from ProShip TAT master' : 'Carried over from Pricing module'} />
        <StatCard label="Account Codes" value={accountCodes} sub="CSVs to push to ProShip" />
        {isExisting ? (
          <StatCard label="Last updated" value="—" sub="Most recent TAT publish" />
        ) : (
          <StatCard label="Service Types" value={`${(selectedSTs || []).length} / 20`} sub="Resolved by Operating Profile" />
        )}
        {!isExisting && (
          <StatCard label="Zones" value={ZONES.length} sub={ZONES.map((z) => z.name.split('·').pop().trim()).join(' · ')} />
        )}
      </Stack>

      <Box>
        <Link component="button" type="button" underline="hover" variant="caption" sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }} onClick={() => dispatch(switchFlow())}>
          <ArrowBackIcon sx={{ fontSize: 14 }} /> Back to dashboard
        </Link>
      </Box>
    </Stack>
  );
}
